/**
 * Sidebar list of documents opened this session.
 *
 * Searchable with fuse.js (the repo's standard fuzzy search) across label, path
 * and id. Each entry can be re-selected or closed; closing only removes it from
 * this list and never touches Firestore.
 */

import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import type { StudioDocument } from "../types";

interface DocumentListProps {
  documents: StudioDocument[];
  selectedPath: string | null;
  dirtyPath: string | null;
  onSelect: (path: string) => void;
  onClose: (path: string) => void;
}

const FUSE_OPTIONS = {
  includeScore: false,
  threshold: 0.3,
  distance: 200,
  keys: ["label", "path", "id"],
};

const DocumentList = ({
  documents,
  selectedPath,
  dirtyPath,
  onSelect,
  onClose,
}: DocumentListProps) => {
  const [query, setQuery] = useState("");

  const fuse = useMemo(() => new Fuse(documents, FUSE_OPTIONS), [documents]);

  const visibleDocuments = useMemo(() => {
    if (query.trim() === "") {
      return documents;
    }
    return fuse.search(query).map((result) => result.item);
  }, [documents, fuse, query]);

  const renderDocument = (doc: StudioDocument) => {
    const isSelected = doc.path === selectedPath;
    const isDirty = doc.path === dirtyPath;
    return (
      <div
        key={doc.path}
        className={`group mb-1 flex items-center gap-1 rounded-md border-l-2 pr-1 transition-colors ${
          isSelected
            ? "border-tertiaryBlue-4 bg-tertiaryBlue-12"
            : "border-transparent hover:bg-primaryGray-16"
        }`}
      >
        <button
          type="button"
          onClick={() => onSelect(doc.path)}
          className="flex min-w-0 flex-1 flex-col items-start gap-0.5 px-3 py-2 text-left"
        >
          <span
            className={`flex w-full items-center gap-2 text-sm ${
              isSelected
                ? "font-semibold text-tertiaryBlue-4"
                : "text-primaryGray-2"
            }`}
          >
            <span className="truncate">{doc.label}</span>
            {isDirty && (
              <span
                title="Unsaved changes"
                className="h-2 w-2 shrink-0 rounded-full bg-secondaryOrange-5"
              />
            )}
          </span>
          <span className="w-full truncate text-tiny text-primaryGray-8">
            {doc.path}
          </span>
        </button>
        <button
          type="button"
          title="Close"
          onClick={() => onClose(doc.path)}
          className="shrink-0 rounded px-1.5 py-1 text-tiny text-primaryGray-8 opacity-0 transition-opacity hover:text-tertiaryRed-4 group-hover:opacity-100"
        >
          ✕
        </button>
      </div>
    );
  };

  return (
    <div className="flex h-full w-64 shrink-0 flex-col overflow-hidden rounded-md border border-primaryGray-15 bg-white">
      <div className="border-b border-primaryGray-15 p-3">
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search open documents..."
          className="w-full rounded-md border border-primaryGray-15 px-3 py-2 text-sm outline-none focus:border-tertiaryBlue-4"
        />
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {documents.length === 0 ? (
          <div className="p-4 text-center text-sm text-primaryGray-8">
            Open a document by path to start editing.
          </div>
        ) : visibleDocuments.length === 0 ? (
          <div className="p-4 text-center text-sm text-primaryGray-8">
            No open documents match your search.
          </div>
        ) : (
          visibleDocuments.map(renderDocument)
        )}
      </div>
    </div>
  );
};

export default DocumentList;
